import { useApiQuery } from '../useApiQuery';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewsV2Endpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import type { ReviewFunnelBaseline } from '@/types/review';

/**
 * The review funnel baseline for one window (spec §9).
 *
 * `from` and `to` are required rather than optional, mirroring the endpoint:
 * it rejects a missing bound instead of inventing one, because a baseline whose
 * window depends on when it happened to be run is not a baseline. Callers get
 * their window from `funnelWindowFor`, which is also what enforces the 366-day
 * cap the endpoint would otherwise answer with a 400.
 *
 * No `refetchInterval`, unlike `useReviewStats`: re-running thirteen concurrent
 * counts every thirty seconds is not free, and most of this response is a
 * historical count that will not move.
 *
 * But part of it is NOT fixed by the window. `posted`, `unreadablePlatforms`
 * and `awaitingFirstSyncPlatforms` describe what we can currently read back,
 * and that changes when a platform finishes its first sync — so a tenant who
 * opened this tab mid-onboarding would sit on "first sync has not run yet"
 * indefinitely while the tab stayed mounted. Refetching on focus fixes that;
 * `staleTime` is what stops it becoming the polling this deliberately avoids,
 * since a focus refetch only fires for a query that has gone stale.
 *
 * Raised in review on #2684.
 */
export function useReviewFunnelBaseline(
  window: { from: string; to: string },
  options: { enabled?: boolean } = {},
) {
  return useApiQuery<ReviewFunnelBaseline>({
    // Off for a window the endpoint would reject. `funnelWindowFor` still hands
    // back legal bounds for a degenerate custom range so the type stays simple,
    // so without this the screen would fetch a year of data for a range the user
    // did not ask for and then have to explain the answer away.
    enabled: options.enabled ?? true,
    queryKey: reviewKeys.funnel(window),
    // Returned UNWRAPPED, unlike `/v2/reviews/stats` next door, which returns
    // `{ data }`. `ReviewMetricsController.baseline` returns the service result
    // directly and there is no global response interceptor, so reaching for
    // `.data` here would hand every consumer `undefined`.
    queryFn: ({ signal }) =>
      apiGet<ReviewFunnelBaseline>(
        operationsClient,
        reviewsV2Endpoints.funnelBaseline(),
        { params: { from: window.from, to: window.to }, signal },
      ),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    showErrorToast: false,
  });
}
